import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FaPrint } from "react-icons/fa";
import { useTranslation } from "react-i18next";

/**
 * Reusable preview modal that matches the Income Statement UI/logic.
 * Handles zooming (buttons + Ctrl/⌘ + wheel), drag-to-pan when zoomed in,
 * and optional print action.
 */
const PdfPreviewModal = ({
  isOpen,
  title,
  onClose,
  onPrint,
  isGeneratingPdf = false,
  children,
  contentWidth = "794px",
}) => {
  const { t } = useTranslation("global");
  const [previewZoom, setPreviewZoom] = useState(1.0);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!isOpen) {
      setPreviewZoom(1.0);
      setPreviewPosition({ x: 0, y: 0 });
      setIsDragging(false);
      return;
    }
    // Reset zoom to 1.0 when modal opens
    setPreviewZoom(1.0);
    setPreviewPosition({ x: 0, y: 0 });

    const handleMouseMove = e => {
      if (!isDragging) return;
      setPreviewPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragStart, isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        zIndex: 99999,
        display: "flex",
        flexDirection: "column",
        padding: "20px",
        overflow: "hidden",
      }}
      onClick={e => {
        if (e.target === e.currentTarget) {
          onClose?.();
        }
      }}
      onWheel={e => {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          const delta = e.deltaY > 0 ? -0.1 : 0.1;
          setPreviewZoom(prev => Math.max(0.3, Math.min(2, prev + delta)));
        }
      }}
    >
      <div
        style={{
          backgroundColor: "#1a1f2e",
          borderRadius: "12px",
          padding: "20px",
          width: "100%",
          height: "100%",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        onClick={e => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            flexShrink: 0,
          }}
        >
          <h2 style={{ color: "white", margin: 0, fontSize: "20px" }}>
            {title}
          </h2>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                marginRight: "10px",
              }}
            >
              <button
                onClick={() =>
                  setPreviewZoom(prev => Math.max(0.3, prev - 0.1))
                }
                style={{
                  backgroundColor: "#444",
                  color: "white",
                  border: "none",
                  borderRadius: 6,
                  padding: "6px 12px",
                  cursor: "pointer",
                  fontSize: "18px",
                  fontWeight: "bold",
                }}
                title={t("common.zoomOut", { defaultValue: "تصغير" })}
              >
                −
              </button>
              <span
                style={{
                  color: "white",
                  minWidth: "60px",
                  textAlign: "center",
                  fontSize: "14px",
                }}
              >
                {Math.round(previewZoom * 100)}%
              </span>
              <button
                onClick={() => setPreviewZoom(prev => Math.min(2, prev + 0.1))}
                style={{
                  backgroundColor: "#444",
                  color: "white",
                  border: "none",
                  borderRadius: 6,
                  padding: "6px 12px",
                  cursor: "pointer",
                  fontSize: "18px",
                  fontWeight: "bold",
                }}
                title={t("common.zoomIn", { defaultValue: "تكبير" })}
              >
                +
              </button>
              <button
                onClick={() => {
                  setPreviewZoom(1.0);
                  setPreviewPosition({ x: 0, y: 0 });
                }}
                style={{
                  backgroundColor: "#444",
                  color: "white",
                  border: "none",
                  borderRadius: 6,
                  padding: "6px 12px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
                title={t("common.fitToScreen", {
                  defaultValue: "ملاءمة الشاشة",
                })}
              >
                {t("common.fit", { defaultValue: "ملاءمة" })}
              </button>
            </div>
            {onPrint && (
              <button
                onClick={onPrint}
                disabled={isGeneratingPdf}
                style={{
                  backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
                  color: "white",
                  border: "none",
                  borderRadius: 8,
                  padding: "10px 20px",
                  cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  opacity: isGeneratingPdf ? 0.6 : 1,
                }}
              >
                <FaPrint />{" "}
                {isGeneratingPdf
                  ? t("common.generating", { defaultValue: "جاري الإنشاء..." })
                  : t("common.print", { defaultValue: "طباعة" })}
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                backgroundColor: "#666",
                color: "white",
                border: "none",
                borderRadius: 8,
                padding: "10px 20px",
                cursor: "pointer",
              }}
            >
              {t("common.close", { defaultValue: "إغلاق" })}
            </button>
          </div>
        </div>
        <div
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            padding: "20px",
            overflow: previewZoom <= 1.0 ? "auto" : "hidden",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: previewZoom <= 1.0 ? "flex-start" : "center",
            justifyContent: previewZoom > 1.0 ? "center" : "flex-start",
            minHeight: 0,
            position: "relative",
            cursor:
              previewZoom > 1.0 && isDragging
                ? "grabbing"
                : previewZoom > 1.0
                  ? "grab"
                  : "default",
          }}
          onMouseDown={e => {
            if (previewZoom > 1.0 && e.button === 0) {
              setIsDragging(true);
              setDragStart({
                x: e.clientX - previewPosition.x,
                y: e.clientY - previewPosition.y,
              });
            }
          }}
          onDragStart={e => previewZoom > 1.0 && e.preventDefault()}
        >
          <div
            style={{
              transform:
                previewZoom > 1.0
                  ? `translate(${previewPosition.x}px, ${previewPosition.y}px) scale(${previewZoom})`
                  : `scale(${previewZoom})`,
              transformOrigin:
                previewZoom <= 1.0 ? "top left" : "center center",
              transition: isDragging ? "none" : "transform 0.2s ease",
              width: contentWidth,
              margin: previewZoom <= 1.0 ? "0 auto" : undefined,
              userSelect: previewZoom > 1.0 ? "none" : "auto",
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PdfPreviewModal;
